import { forwardRef, memo, useCallback } from 'react';
import type { ComponentPropsWithoutRef, ReactElement } from 'react';

import { cx } from '@/utils/cx';

import { useActionContext } from '../CategoryContext';

import styles from './CategoryGroupTitle.module.scss';

export interface CategoryGroupTitleProps extends Omit<ComponentPropsWithoutRef<'button'>, 'children'> {
  /** 그룹 타이틀 텍스트. 미지정 시 icon만 노출하며 icon 크기 높이 24 고정. */
  label?: string;
  /** 카테고리 아이콘 (label 있을 때 24×24). string이면 <img>로 자동 렌더링. */
  icon?: ReactElement | string;
  /** onAction 인자 */
  action: string;
  /** true이면 클릭 가능 + arrow 노출. action과 독립적으로 제어. */
  navigable?: boolean;
  /** onAction 콜백에 전달할 메타데이터 */
  meta?: Record<string, unknown>;
}

export const CategoryGroupTitle = memo(
  forwardRef<HTMLButtonElement, CategoryGroupTitleProps>(
    (
      { label, icon, action, navigable = false, meta = {}, onClick, className, 'aria-label': ariaLabel, ...rest },
      ref,
    ) => {
      const { onAction } = useActionContext();

      const handleClick = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
          onClick?.(e);
          if (!e.defaultPrevented) {
            onAction?.(action, meta, e);
          }
        },
        [onClick, action, onAction, meta],
      );

      let iconElement: ReactElement | undefined;
      if (typeof icon === 'string') {
        iconElement =
          label !== undefined ? <img alt="" height={24} src={icon} width={24} /> : <img alt="" src={icon} />;
      } else {
        iconElement = icon;
      }

      return (
        <button
          aria-disabled={!navigable || undefined}
          aria-label={ariaLabel ?? label ?? action}
          className={cx(styles.title, !navigable && styles.titleDisabled, className)}
          data-action={action}
          onClick={navigable ? handleClick : undefined}
          ref={ref}
          tabIndex={navigable ? 0 : -1}
          type="button"
          {...rest}
        >
          {iconElement ? (
            <span className={label !== undefined ? styles.icon : styles.iconOnly}>{iconElement}</span>
          ) : null}
          {label !== undefined ? <span className={styles.label}>{label}</span> : null}
          {navigable ? (
            <span aria-hidden="true" className={styles.arrow}>
              <svg fill="none" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M6 3L11 8L6 13"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                />
              </svg>
            </span>
          ) : null}
        </button>
      );
    },
  ),
);

CategoryGroupTitle.displayName = 'Category.GroupTitle';
